import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "회사 소개", href: "#about" },
  { label: "연혁", href: "#history" },
  { label: "비전·사업 영역", href: "#vision" },
  { label: "제품·서비스", href: "#products" },
  { label: "솔루션", href: "#solutions" },
  { label: "Demo", href: "/demo", isRoute: true },
  { label: "협력 문의", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && menuOpen) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-dark/95 backdrop-blur-md border-b border-cyan-900/40"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16" aria-label="메인 네비게이션">
        <button
          type="button"
          className="flex items-center cursor-pointer bg-transparent border-none"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="CITY-AI 홈으로 이동"
        >
          <img
            src="/cityai-logo-full.png"
            alt="City AI 로고"
            className="h-12 w-auto object-contain"
          />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isRoute ? (
              <motion.div key={link.href} whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to={link.href}
                  className={`text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide ${
                    location.pathname === link.href
                      ? "text-cyan-400"
                      : "text-gray-300 hover:text-cyan-400"
                  }`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ) : (
              <motion.a
                key={link.href}
                href={isHome ? link.href : `/${link.href}`}
                className="text-sm text-gray-300 hover:text-cyan-400 transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide"
                onClick={(e) => {
                  if (!isHome) return;
                  e.preventDefault();
                  handleNav(link.href);
                }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {link.label}
              </motion.a>
            )
          )}
          <motion.div
            whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(0, 229, 255, 0.3)" }}
            whileTap={{ scale: 0.95 }}
          >
            {isHome ? (
              <button
                type="button"
                className="text-sm px-4 py-2 border border-cyan-500 text-cyan-400 rounded-md hover:bg-cyan-500/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
                onClick={() => handleNav("#contact")}
              >
                문의하기
              </button>
            ) : (
              <Link
                to="/#contact"
                className="text-sm px-4 py-2 border border-cyan-500 text-cyan-400 rounded-md hover:bg-cyan-500/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                문의하기
              </Link>
            )}
          </motion.div>
        </div>

        <button
          type="button"
          className="lg:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "메뉴 닫기" : "메뉴 열기"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          <motion.span
            className="block h-0.5 bg-gray-300 w-6"
            animate={{
              rotate: menuOpen ? 45 : 0,
              y: menuOpen ? 8 : 0,
            }}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block h-0.5 bg-gray-300 w-5"
            animate={{
              opacity: menuOpen ? 0 : 1,
              width: menuOpen ? 0 : 20,
            }}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block h-0.5 bg-gray-300 w-6"
            animate={{
              rotate: menuOpen ? -45 : 0,
              y: menuOpen ? -8 : 0,
            }}
            transition={{ duration: 0.3 }}
          />
        </button>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden bg-brand-dark/95 backdrop-blur-lg border-t border-cyan-900/30 overflow-hidden"
            role="menu"
            aria-label="모바일 메뉴"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link, index) =>
                link.isRoute ? (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setMenuOpen(false)}
                      className={`text-sm transition-colors py-1 cursor-pointer whitespace-nowrap ${
                        location.pathname === link.href
                          ? "text-cyan-400"
                          : "text-gray-300 hover:text-cyan-400"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={link.href}
                    href={isHome ? link.href : `/${link.href}`}
                    className="text-sm text-gray-300 hover:text-cyan-400 transition-colors py-1 cursor-pointer whitespace-nowrap"
                    onClick={(e) => {
                      if (isHome) {
                        e.preventDefault();
                        handleNav(link.href);
                      } else {
                        setMenuOpen(false);
                      }
                    }}
                    role="menuitem"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.label}
                  </motion.a>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
